import React, { useState } from 'react'
import { Link, useLocation } from 'wouter'
import {
  Home,
  Users,
  Bell,
  Calendar,
  Building2,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Users as UserGroup,
  MessageSquare,
  BookOpen,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useUnreadNotificationCount } from '@/hooks/useNotifications'
import { useConnectedStudents, usePendingRequests } from '@/hooks/useCounselorData'
import { useCounselorSessions, useSessionRequests } from '@/hooks/useSessions'
import { useChatSessionsList } from '@/hooks/useChatSessionsList'
import { cn } from '@/lib/utils'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  badge?: number
  children?: NavItem[]
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [location] = useLocation()
  const { user, isAgencyAdmin, isLoading } = useAuth()
  const [expandedItems, setExpandedItems] = useState<string[]>(['agency'])

  // Get dynamic data for badges and stats
  const { data: unreadNotifications } = useUnreadNotificationCount(user?.id)
  const { data: connectedStudents } = useConnectedStudents(user?.id)
  const { data: sessionRequests } = useSessionRequests(user?.id)
  const { data: pendingRequests } = usePendingRequests(user?.id)
  const { data: sessions } = useCounselorSessions({ status: 'active' })
  const { totalUnreadCount } = useChatSessionsList()

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev => 
      prev.includes(itemName) 
        ? prev.filter(item => item !== itemName)
        : [...prev, itemName]
    )
  }

  const baseNavItems: NavItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home
    },
    {
      name: 'Students',
      href: '/students',
      icon: Users,
      badge: connectedStudents?.students?.length || 0
    },
    {
      name: 'Messages',
      href: '/messages',
      icon: MessageSquare,
      badge: totalUnreadCount || 0
    },
    {
      name: 'Sessions',
      href: '/sessions',
      icon: Calendar,
      badge: sessionRequests?.requests?.length || 0
    },
    {
      name: 'Resources',
      href: '/resources',
      icon: BookOpen
    },
    {
      name: 'Notifications',
      href: '/notifications',
      icon: Bell,
      badge: unreadNotifications?.count || 0
    }
  ]

  const agencyManagementItem: NavItem = {
    name: 'Agency Management',
    href: '/agency',
    icon: Building2,
    children: [
      {
        name: 'Dashboard',
        href: '/agency',
        icon: BarChart3
      },
      {
        name: 'Students',
        href: '/agency/students',
        icon: Users
      },
      {
        name: 'Counselors',
        href: '/agency/counselors',
        icon: UserGroup
      },
      {
        name: 'Sessions',
        href: '/agency/sessions',
        icon: Calendar
      },
    ]
  }

  // Only show Agency Management to agency admins (and ensure auth is fully loaded)
  const navItems: NavItem[] = (!isLoading && isAgencyAdmin) 
    ? [...baseNavItems, agencyManagementItem]
    : baseNavItems

  const isActive = (href: string) => {
    if (href === '/dashboard' && (location === '/' || location === '/dashboard')) {
      return true
    }
    return location === href || location.startsWith(href + '/')
  }

  const renderNavItem = (item: NavItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.name.toLowerCase().replace(' ', '-'))
    const itemIsActive = isActive(item.href)

    return (
      <div key={item.name}>
        <div className="relative">
          {hasChildren ? (
            <button
              onClick={() => toggleExpanded(item.name.toLowerCase().replace(' ', '-'))}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2 text-left rounded-lg transition-colors",
                level === 0 ? "text-sm font-medium" : "text-sm",
                itemIsActive
                  ? "bg-primary text-white"
                  : "text-gray-700 hover:bg-gray-100",
                level > 0 && "ml-4"
              )}
            >
              <div className="flex items-center space-x-3">
                <item.icon className={cn(
                  "h-5 w-5",
                  level > 0 && "h-4 w-4"
                )} />
                <span>{item.name}</span>
                {item.badge && !itemIsActive && (
                  <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                    {item.badge}
                  </span>
                )}
              </div>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <Link href={item.href}>
              <div
                onClick={onClose}
                className={cn(
                  "flex items-center justify-between px-3 py-2 rounded-lg transition-colors cursor-pointer",
                  level === 0 ? "text-sm font-medium" : "text-sm",
                  itemIsActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100",
                  level > 0 && "ml-4"
                )}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className={cn(
                    "h-5 w-5",
                    level > 0 && "h-4 w-4"
                  )} />
                  <span>{item.name}</span>
                </div>
                {item.badge && !itemIsActive && (
                  <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                    {item.badge}
                  </span>
                )}
              </div>
            </Link>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.children?.map(child => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out",
        "lg:relative lg:translate-x-0 lg:z-auto",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">CounselorPro</h1>
                <p className="text-xs text-gray-600">Dashboard</p>
              </div>
            </div>
          </div>


          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {navItems.map(item => renderNavItem(item))}
          </nav>

          {/* Quick Stats */}
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Today's Summary
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Active Sessions</span>
                  <span className="font-medium text-gray-900">{sessions?.sessions?.length || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">New Messages</span>
                  <span className="font-medium text-gray-900">{totalUnreadCount || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Pending Reviews</span>
                  <span className="font-medium text-gray-900">{Array.isArray(pendingRequests) ? pendingRequests.length : 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}